// Push notifications for the phone app (Capacitor Push Notifications plugin + Firebase Cloud Messaging).
//
// Like utils/native.js this does NOT import any @capacitor package: the native shell provides the
// plugin at runtime (window.Capacitor.Plugins.PushNotifications), so the website build never depends
// on it. If the plugin is not installed in the app build, everything here quietly does nothing.

import { supabase } from '../lib/supabase';
import { isNative, nativePlatform } from './native';

const CHANNEL_ID = 'alerts';
const PREF_KEY = 'ccs_push_pref';     // 'on' | 'off' – what the person chose on the Alerts screen
const TOKEN_KEY = 'ccs_push_token';   // this phone's current push token

const plugin = () => (isNative && typeof window !== 'undefined' && window.Capacitor?.Plugins?.PushNotifications) || null;

const store = {
  get: (k) => { try { return window.localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* optional */ } },
  del: (k) => { try { window.localStorage.removeItem(k); } catch { /* optional */ } },
};

let listenersReady = false;
let tokenWaiter = null;            // { resolve, reject } for the registration in progress
let pendingRoute = null;           // a notification tapped before the app screens were ready
const routeSubs = new Set();
const foregroundSubs = new Set();

const saveToken = async (token) => {
  const { error } = await supabase.rpc('register_push_device', { p_token: token, p_platform: nativePlatform });
  if (error) throw error;
  store.set(TOKEN_KEY, token);
};

const ensureListeners = () => {
  const p = plugin();
  if (!p || listenersReady) return;
  listenersReady = true;

  p.addListener('registration', async (t) => {
    const waiter = tokenWaiter;
    tokenWaiter = null;
    try { await saveToken(t.value); waiter?.resolve(true); } catch (e) { console.error('[push] could not save token', e); waiter?.reject(e); }
  });
  p.addListener('registrationError', (e) => {
    console.error('[push] registration error', e);
    const waiter = tokenWaiter;
    tokenWaiter = null;
    waiter?.reject(new Error(e?.error || 'Registration failed'));
  });
  // App is open on screen: Android does not draw a system notification, so the app shows its own.
  p.addListener('pushNotificationReceived', (n) => { foregroundSubs.forEach(fn => { try { fn(n); } catch { /* ignore */ } }); });
  // The person tapped a notification.
  p.addListener('pushNotificationActionPerformed', (a) => {
    const route = a?.notification?.data?.route || '/alerts';
    if (routeSubs.size) routeSubs.forEach(fn => { try { fn(route); } catch { /* ignore */ } });
    else pendingRoute = route;
  });
};

const registerAndWait = (p) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => { tokenWaiter = null; reject(new Error('Timed out waiting for the phone to register')); }, 20000);
  tokenWaiter = {
    resolve: (v) => { clearTimeout(timer); resolve(v); },
    reject: (e) => { clearTimeout(timer); reject(e); },
  };
  Promise.resolve(p.register()).catch((e) => { clearTimeout(timer); tokenWaiter = null; reject(e); });
});

export const pushAvailable = () => !!plugin();

// 'unsupported' | 'ask' (never asked) | 'denied' (blocked in phone settings) | 'off' (turned off here) | 'on'
export const getPushState = async () => {
  const p = plugin();
  if (!p) return 'unsupported';
  try {
    const { receive } = await p.checkPermissions();
    if (receive === 'denied') return 'denied';
    if (receive !== 'granted') return 'ask';
    return store.get(PREF_KEY) === 'off' ? 'off' : 'on';
  } catch {
    return 'unsupported';
  }
};

// Asks for permission if needed, then registers this phone so it can receive alerts.
export const enablePush = async () => {
  const p = plugin();
  if (!p) return { ok: false, reason: 'unsupported' };
  try {
    let { receive } = await p.checkPermissions();
    if (receive !== 'granted') ({ receive } = await p.requestPermissions());
    if (receive !== 'granted') return { ok: false, reason: 'denied' };

    try {
      await p.createChannel?.({
        id: CHANNEL_ID, name: 'Fleet alerts', description: 'Insurance, road tax, services and other reminders',
        importance: 4, visibility: 1, vibration: true,
      });
    } catch { /* channels only exist on Android 8+ */ }

    ensureListeners();
    await registerAndWait(p);
    store.set(PREF_KEY, 'on');
    return { ok: true };
  } catch (e) {
    console.error('[push] enable failed', e);
    return { ok: false, reason: 'failed', message: e?.message };
  }
};

// Stops alerts on this phone only.
export const disablePush = async () => {
  const p = plugin();
  store.set(PREF_KEY, 'off');
  await forgetPushDevice();
  try { await p?.unregister?.(); } catch { /* ignore */ }
};

// Called when the app opens (signed in): if alerts were already allowed, quietly refresh the token.
export const autoRegisterPush = async () => {
  const p = plugin();
  if (!p || store.get(PREF_KEY) === 'off') return;
  try {
    const { receive } = await p.checkPermissions();
    if (receive !== 'granted') return;
    ensureListeners();
    await registerAndWait(p);
  } catch (e) {
    console.error('[push] auto register failed', e);
  }
};

// Removes this phone from the signed-in account (used on sign out, so the next person who signs
// in on this phone does not get the previous person's alerts).
export const forgetPushDevice = async () => {
  const token = store.get(TOKEN_KEY);
  if (!token) return;
  try { await supabase.rpc('unregister_push_device', { p_token: token }); } catch { /* ignore */ }
  store.del(TOKEN_KEY);
};

export const sendTestPush = async () => {
  const { data, error } = await supabase.functions.invoke('send-test-push', { body: {} });
  if (error) throw new Error(error.message || 'Could not send the test');
  return data;
};

// Opens the right screen when a notification is tapped.
export const onPushOpen = (fn) => {
  routeSubs.add(fn);
  if (pendingRoute) { const r = pendingRoute; pendingRoute = null; fn(r); }
  return () => routeSubs.delete(fn);
};

// Notification arrived while the app is on screen.
export const onPushForeground = (fn) => {
  foregroundSubs.add(fn);
  return () => foregroundSubs.delete(fn);
};

// Call once when the signed-in phone shell opens.
export const startPush = () => {
  if (!plugin()) return;
  ensureListeners();
  autoRegisterPush();
};
